import React, { useState, useCallback } from 'react';
import { searchMetadata } from '../api/ticketsApi';
import './MetadataExplorer.css';
import config from '../config';

const { environment } = config;

function MetadataExplorer({ accessToken, environments }) {
  const [selectedEnvironment, setSelectedEnvironment] = useState(environments[0] || environment);
  const [metadataPath, setMetadataPath] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = useCallback(async () => {
    if (!metadataPath.trim()) {
      setError('Please enter a metadata path to search');
      return;
    }

    if (!accessToken) {
      setError('No access token available');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const responseData = await searchMetadata(
        selectedEnvironment,
        metadataPath,
        accessToken
      );

      // Detect if response is JSON or XML
      const isXml = responseData.trim().startsWith('<');
      const contentType = isXml ? 'application/xml' : 'application/json';
      const fileExtension = isXml ? 'xml' : 'json';

      // Create a blob from the response
      const blob = new Blob([responseData], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);

      // Open in new tab
      const newTab = window.open(blobUrl, '_blank');
      if (newTab) {
        // Set a title for the new tab
        newTab.document.title = `Metadata - ${selectedEnvironment}`;
      }

      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);

      console.log('✓ Metadata response opened in new tab');
    } catch (err) {
      setError(err.message || 'Failed to search metadata');
      console.error('Metadata search error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedEnvironment, metadataPath, accessToken]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <section className="metadata-explorer">
      <div className="metadata-container">
        <h2>Metadata Explorer</h2>

        <div className="metadata-controls">
          <div className="control-group">
            <label htmlFor="environment-select">Environment</label>
            <select
              id="environment-select"
              value={selectedEnvironment}
              onChange={(e) => setSelectedEnvironment(e.target.value)}
              className="environment-select"
            >
              {environments.map((env) => (
                <option key={env} value={env}>
                  {env}
                </option>
              ))}
            </select>
          </div>

          <div className="control-group">
            <label htmlFor="metadata-search">Metadata Path</label>
            <input
              id="metadata-search"
              type="text"
              value={metadataPath}
              onChange={(e) => setMetadataPath(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter metadata path to search..."
              className="metadata-search"
              disabled={loading}
            />
          </div>

          <div className="control-group button-group">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="btn btn-search"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        {error && (
          <div className="metadata-error">
            <p>{error}</p>
          </div>
        )}

        {loading && (
          <div className="metadata-loading">
            <div className="spinner"></div>
            <p>Searching metadata...</p>
          </div>
        )}
      </div>
    </section>
  );
}

export default MetadataExplorer;
